import { trpc } from '../lib/trpc';

interface EntityTypeBreakdown {
  entityType: string;
  count: number;
  totalSize: number;
}

interface MediaWithUploader {
  id: string;
  filename: string;
  entityType: string;
  sizeBytes: number;
  isPublished: boolean;
}

interface AuditLog {
  id: string;
  action: string;
  timestamp: string;
  performedBy: {
    name?: string;
    email?: string;
  };
}

export function AdminMediaTest() {
  // Test the admin endpoints
  const { data: orphanReport, isPending: orphanLoading, error: orphanError } = 
    trpc.media.getOrphanReport.useQuery();

  const { data: deletedMedia, isPending: deletedLoading, error: deletedError } = 
    trpc.media.listDeletedMedia.useQuery({ limit: 5 });

  const { data: auditLogs, isPending: auditLoading, error: auditError } = 
    trpc.media.getAuditLogs.useQuery({ limit: 5 });

  const { data: allMedia, isPending: allMediaLoading, error: allMediaError } = 
    trpc.media.listAllMedia.useQuery({ limit: 5 });

  return (
    <div className="max-w-4xl 2xl:max-w-6xl mx-auto p-6 space-y-8">
      <h1 className="text-3xl font-bold text-gray-900">Admin Media API Test</h1>
      
      {/* Orphan Report Test */}
      <div className="bg-gray-50 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Orphan Report</h2>
        {orphanLoading && <p>Loading orphan report...</p>}
        {orphanError && <p className="text-red-600">Error: {orphanError.message}</p>}
        {orphanReport && (
          <div>
            <p>Total orphans: {orphanReport.summary.totalOrphans}</p>
            <p>Total size: {formatBytes(orphanReport.summary.totalSize)}</p>
            <div className="mt-2">
              <h3 className="font-medium">By Entity Type:</h3>
              {orphanReport.summary.byEntityType.map((item: EntityTypeBreakdown) => (
                <div key={item.entityType} className="ml-4">
                  {item.entityType}: {item.count} files ({formatBytes(item.totalSize)})
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Deleted Media Test */}
      <div className="bg-gray-50 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Deleted Media</h2>
        {deletedLoading && <p>Loading deleted media...</p>}
        {deletedError && <p className="text-red-600">Error: {deletedError.message}</p>}
        {deletedMedia && (
          <div>
            <p>Total deleted: {deletedMedia.total}</p>
            <div className="mt-2">
              <h3 className="font-medium">Recent deleted media:</h3>
              {(deletedMedia.items as unknown as MediaWithUploader[]).map((media) => (
                <div key={media.id} className="ml-4 text-sm">
                  {media.filename} - {media.entityType} - {formatBytes(media.sizeBytes)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Audit Logs Test */}
      <div className="bg-gray-50 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Audit Logs</h2>
        {auditLoading && <p>Loading audit logs...</p>}
        {auditError && <p className="text-red-600">Error: {auditError.message}</p>}
        {auditLogs && (
          <div>
            <p>Total logs: {auditLogs.total}</p>
            <div className="mt-2">
              <h3 className="font-medium">Recent logs:</h3>
              {auditLogs.logs.map((log: AuditLog) => (
                <div key={log.id} className="ml-4 text-sm">
                  {log.action} by {log.performedBy.name || log.performedBy.email} - {new Date(log.timestamp).toLocaleString()}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* All Media Test */}
      <div className="bg-gray-50 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">All Media</h2>
        {allMediaLoading && <p>Loading all media...</p>}
        {allMediaError && <p className="text-red-600">Error: {allMediaError.message}</p>}
        {allMedia && (
          <div>
            <p>Total active media: {allMedia.total}</p>
            <div className="mt-2">
              <h3 className="font-medium">Recent media:</h3>
              {(allMedia.items as unknown as MediaWithUploader[]).map((media) => (
                <div key={media.id} className="ml-4 text-sm">
                  {media.filename} - {media.entityType} - {formatBytes(media.sizeBytes)} - 
                  {media.isPublished ? ' Published' : ' Unpublished'}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper function to format bytes
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}